import { citiesOptionsConverter } from 'data/convertors/general.converters';
import {
  kitOrderingShippingAddressConverter,
  registerEventConverter,
  notifyMeConverter,
  eventsConverter,
  eventConverter,
  registrationFormDataConverter,
  lightBoxConverter,
  registerMultipleEventConverter,
} from 'data/convertors/events.convertors';
import { TOption } from 'data/types/generic.types';
import {
  TKitShippingAddressEditUI,
  TEventDB,
  TEventRegisterDataUI,
  TEventUI,
  TSubscribeDataUI,
  TEventDetailUI,
  TEventDetailDB,
  TEventRegisterResponseUI,
  TEventRegisterResponseDB,
  TRegistrationFormDataUI,
  TRegistrationFormDataDB,
  TLightBoxUI,
  TLightBoxDB,
  TEventMultipleRegisterDataUI,
  TEventMultipleRegisterDataDB,
  TEventMultipleRegisterResponseUI,
  TEventMultipleRegisterResponseDB,
} from 'data/types/events.types';
import {
  EEventsUrls,
  meetingLinkUrl,
  getRegistrationModalDataUrl,
  getRegistrationFreeSlotsUrl,
} from 'data/urls/events.url';
import { emptySplitApi } from 'redux/helpers/slice.helpers';

const baseUrl = `${EEventsUrls.EVENTS}`;

export const eventsApi = emptySplitApi('events', [
  'events',
  'event',
  'eventsList',
  'shipping-detail',
  'free-slot',
]).injectEndpoints({
  endpoints: build => ({
    registerEvent: build.mutation<
      TEventRegisterResponseUI,
      TEventRegisterDataUI
    >({
      query(request) {
        const { recaptchaResponse, userLanguage, ...body } =
          registerEventConverter.toDb(request);
        return {
          url: `${baseUrl}/${EEventsUrls.REGISTRATION}`,
          method: 'POST',
          headers: {
            'g-recaptcha-response': recaptchaResponse,
            'x-user-language': userLanguage,
          },
          body,
        };
      },
      invalidatesTags: ['events'],
      transformResponse: (response: TEventRegisterResponseDB) =>
        registerEventConverter.fromDb(response),
    }),
    getEventsList: build.query<TEventUI, string>({
      // @ts-ignore
      query(params) {
        return {
          url: `${baseUrl}/${EEventsUrls.EVENT}`,
          method: 'GET',
          params,
        };
      },
      transformResponse: (data: TEventDB) => eventsConverter.fromDb(data),
      providesTags: ['eventsList'],
    }),
    getEventsById: build.query<TEventDetailUI, string>({
      query(id) {
        return {
          url: `${baseUrl}/${EEventsUrls.EVENT}/${id}`,
          method: 'GET',
        };
      },
      transformResponse: (response: TEventDetailDB) =>
        eventConverter.fromDb(response),
      providesTags: ['event'],
    }),
    getEventCities: build.query<TOption[], void>({
      query() {
        return {
          url: `${baseUrl}/${EEventsUrls.EVENT_CITIES}`,
          method: 'GET',
        };
      },
      transformResponse: (response: string[]) =>
        citiesOptionsConverter.fromDb(response),
    }),
    getShippingDetail: build.query<TEventUI, { parentOrderId: string | null }>({
      query({ parentOrderId }) {
        return {
          url: `${baseUrl}/${EEventsUrls.PARENT_ORDERING}/${EEventsUrls.SHIPPING_DETAILS}/${EEventsUrls.VALIDATE}`,
          method: 'GET',
          params: { parentOrderId },
        };
      },
    }),
    addKitOrderingShippingAddress: build.mutation<
      void,
      TKitShippingAddressEditUI
    >({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.PARENT_ORDERING}/${EEventsUrls.SHIPPING_DETAILS}`,
          method: 'PATCH',
          body: kitOrderingShippingAddressConverter.toDb(body),
        };
      },
    }),
    subscribeMe: build.mutation<void, TSubscribeDataUI>({
      query(request) {
        const { recaptchaResponse, ...body } = notifyMeConverter.toDb(request);
        return {
          url: `${baseUrl}/${EEventsUrls.SUBSCRIBE}`,
          method: 'POST',
          headers: {
            'g-recaptcha-response': recaptchaResponse,
          },
          body,
        };
      },
    }),
    getIdahoCities: build.query<string[], void>({
      query() {
        return {
          url: `${baseUrl}/${EEventsUrls.CITIES}/${EEventsUrls.IDAHO}`,
          method: 'GET',
        };
      },
    }),
    getRegistrationFreeSlots: build.query<
      boolean,
      { eventClassId?: string; eventId: string }
    >({
      query({ eventClassId, eventId }) {
        return {
          url: getRegistrationFreeSlotsUrl,
          method: 'GET',
          params: { eventClassId, eventId },
        };
      },
      providesTags: ['free-slot'],
    }),
    getRegistrationFormData: build.query<
      TRegistrationFormDataUI,
      { eventId: string }
    >({
      query({ eventId }) {
        return {
          url: `${getRegistrationModalDataUrl}/${eventId}`,
          method: 'GET',
        };
      },
      transformResponse: (response: TRegistrationFormDataDB) =>
        registrationFormDataConverter.fromDb(response),
    }),
    getMeetingLink: build.query<string, { eventId: string }>({
      query({ eventId }) {
        return {
          url: meetingLinkUrl,
          method: 'GET',
          params: { eventId },
        };
      },
    }),
    getLightBox: build.query<TLightBoxUI[], void>({
      query() {
        return {
          url: `${EEventsUrls.EVENTS}/${EEventsUrls.LIGHT_BOX}`,
          method: 'GET',
        };
      },
      transformResponse: (data: TLightBoxDB[]) => {
        return data?.length
          ? data.map(item => lightBoxConverter.fromDb(item))
          : [];
      },
    }),
    registerMultipleEvent: build.mutation<
      TEventMultipleRegisterResponseUI,
      TEventMultipleRegisterDataUI
    >({
      query(request) {
        const { registrationDetails } =
          registerMultipleEventConverter.toDb(request);
        const cleanedDetails = (registrationDetails as any[]).map(d => {
          const { eventClassId, ...rest } = d || {};
          return eventClassId ? d : rest;
        });

        const firstRegistration = cleanedDetails[0] as any;

        const body: TEventMultipleRegisterDataDB = {
          registrationDetails:
            cleanedDetails as unknown as TEventMultipleRegisterDataDB['registrationDetails'],
        };

        return {
          url: `${baseUrl}/${EEventsUrls.REGISTRATION}/multiple`,
          method: 'POST',
          headers: {
            'g-recaptcha-response': firstRegistration.recaptchaResponse,
            'x-user-language': firstRegistration.userLanguage,
          },
          body,
        };
      },
      invalidatesTags: ['events'],
      transformResponse: (response: TEventMultipleRegisterResponseDB) =>
        registerMultipleEventConverter.fromDb(response),
    }),
  }),
});

export const {
  useRegisterEventMutation,
  useGetEventsByIdQuery,
  useGetEventsListQuery,
  useGetEventCitiesQuery,
  useGetShippingDetailQuery,
  useAddKitOrderingShippingAddressMutation,
  useSubscribeMeMutation,
  useGetIdahoCitiesQuery,
  useLazyGetRegistrationFreeSlotsQuery,
  useGetRegistrationFreeSlotsQuery,
  useGetRegistrationFormDataQuery,
  useGetMeetingLinkQuery,
  useGetLightBoxQuery,
  useRegisterMultipleEventMutation,
} = eventsApi;
